/**
 * Dev identity (CLAUDE.md "Dev auth", docs/06 §2.4).
 *
 * On first load the app prompts for a display name and stores it in
 * localStorage. The (URI-encoded) name is sent as the `x-dev-user` header on
 * every fetch and as the `?user=` query parameter on the collaboration
 * WebSocket URL, so REST and WS resolve the same server-side Identity.
 * Production replaces all of this with IAP — nothing here ships auth logic.
 */
import { canonicalAccountKey, type PresenceUser } from "@kola/shared";

const NAME_KEY = "kola.displayName";

export function getStoredName(): string | null {
  try {
    const name = localStorage.getItem(NAME_KEY);
    return name && name.trim() ? name.trim() : null;
  } catch {
    return null;
  }
}

export function storeName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name.trim());
  } catch {
    // Private-mode storage failures degrade to prompting again next load.
  }
}

/**
 * The dev-auth IDENTITY: canonicalized (case/whitespace/Unicode-composition-
 * insensitive) so logging in with the same account on any device resolves to
 * the same user. This is exactly what `AUTH_MODE=dev` computes server-side
 * (`core/auth/dev.ts`), so `currentUser().id` and `request.identity.id` are
 * the SAME STRING — which is what lets ids written into the doc (a comment's
 * `authorId`, an element's `by`) be compared against a server-resolved
 * identity at all.
 */
export function devUserId(name: string): string {
  return canonicalAccountKey(name);
}

/**
 * The identity as it must be written into a URL or an HTTP header — the same
 * id, percent-encoded so a non-ASCII display name survives header rules and a
 * `?user=` query parameter round-trips through URLSearchParams.
 *
 * Deliberately SEPARATE from the identity itself. Conflating the two is a
 * trap: dev auth decodes the header back before canonicalizing, so an encoded
 * `kim%40local` in the doc would never equal the `kim@local` the server
 * resolves, and every id comparison that crosses that boundary — "is this
 * mention me", the F-110 reply fan-out — would silently miss.
 */
export function identityTransportValue(): string {
  return encodeURIComponent(currentUser().id);
}

/**
 * Deterministic display color, hashed from the CANONICAL account key so the
 * same account keeps one color regardless of how the name was typed.
 */
export function colorForName(name: string): string {
  const key = canonicalAccountKey(name);
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const hue = ((hash % 360) + 360) % 360;
  return hslToHex(hue, 62, 44);
}

export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.charAt(0) ?? "?";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * Server-verified identity (AUTH_MODE=firebase) — set once by the App shell
 * after /api/me resolves, before any route renders. Overrides the dev
 * name-prompt identity everywhere `currentUser()` is read. The color hashes
 * from the EMAIL (not the uid) so a user keeps the same color they had under
 * dev/IAP identities, which were email-keyed.
 */
let activeIdentity: PresenceUser | null = null;

export function setActiveIdentity(identity: { id: string; email: string; name: string }): void {
  activeIdentity = {
    id: identity.id,
    name: identity.name,
    color: colorForName(identity.email),
  };
}

/** The local user's presence identity (F-41). */
export function currentUser(): PresenceUser {
  if (activeIdentity) return activeIdentity;
  const name = getStoredName() ?? "Anonymous";
  return {
    id: devUserId(name),
    name,
    color: colorForName(name),
  };
}

/** hex output so core/color.ts withAlpha can parse it. */
function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100;
  const ln = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number) => ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}
